import { Signal } from '@angular/core';
import { QuizQuestionEntry } from '@services/experiment/experiment.service.types';
import { QuizAnswerOutcome, QuizOption, QuizQuestionLayout } from '@services/quiz-chat-service/quiz-chat.types';

/** One quiz screen: the variant's entry plus whatever answer the server holds for it. */
export interface QuizEngineEntry {
  questionId: string;
  gate?: string;
  answer: string | string[] | null;
}

/** One card of the photo pair, resolved and ready to render. */
export interface QuizPhotoSlot {
  canonicalUrl: string;
  checkupId: string;
  label: string;
  type: string;
  url: string;
}

export interface QuizPhotoPair {
  alternate: QuizPhotoSlot | null;
  current: QuizPhotoSlot | null;
  showPair: boolean;
}

/** A question's inputs, split by the role each plays in choosing a layout. */
export interface QuizQuestionInputs {
  buttonOptions: QuizOption[];
  imageOptions: QuizOption[];
  photoOption: QuizOption | null;
  textInput: QuizOption | null;
}

export interface QuizEngineConfig {
  consultationSessionId: string;
  regimenClass: string;
  entries?: QuizQuestionEntry[];
  answeredFirstQuestionIds?: string[];
  onEntriesExhausted?: () => Promise<void>;
}

/** Persisted per consultation session: questionId → the answer the server stored. */
export type QuizAnswerMap = Record<string, string | string[]>;

export const QUIZ_ANSWERS_STORAGE_KEY = 'CureSkin/consultationQuizAnswers';

export const QUIZ_SEEN_SCREENS_STORAGE_KEY = 'CureSkin/consultationQuizSeenScreens';

export const QUIZ_AUTO_ANSWER_QUESTION_IDS: ReadonlySet<string> = new Set(['Welcome', 'whatDoCureSkinMembersSay']);

export const QUIZ_CARRIED_OVER_QUESTION_IDS: ReadonlySet<string> = new Set([
  'FemalePregOrNewMom',
  'communicationLanguagePreference',
]);

export interface QuizClientOnlyScreen {
  absorbs: string[];
  layout: QuizQuestionLayout;
  questionId: string;
}

export const QUIZ_VIEW_BOTH_PHOTOS_ID = 'viewBothPhotos';

export const QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID = 'mandatoryHairTopPhoto';

export const QUIZ_CLIENT_ONLY_SCREENS: ReadonlyArray<QuizClientOnlyScreen> = [{
  absorbs: ['HairfallPhotoTop', 'HairfallPhoto'],
  layout: QuizQuestionLayout.PhotoReview,
  questionId: QUIZ_VIEW_BOTH_PHOTOS_ID,
}, {
  absorbs: [],
  layout: QuizQuestionLayout.PhotoUpload,
  questionId: QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID,
}];

/** Whether a capture made while the quiz showed `lastBotQuestionId` answers `questionId`. */
export const shouldAnswerPhotoQuestion = (lastBotQuestionId: string, questionId: string): boolean =>
  lastBotQuestionId === questionId
  || (questionId === 'HairfallPhotoTop' && lastBotQuestionId === QUIZ_MANDATORY_HAIR_TOP_PHOTO_ID);

export const findClientOnlyScreen = (questionId: string): QuizClientOnlyScreen | undefined =>
  QUIZ_CLIENT_ONLY_SCREENS.find((screen) => screen.questionId === questionId);

export const QUIZ_PHOTO_QUESTION_IDS: ReadonlySet<string> = new Set(
  QUIZ_CLIENT_ONLY_SCREENS.flatMap((screen) => screen.absorbs),
);

export interface QuizEngine {
  readonly loading: Signal<boolean>;
  readonly questionText: Signal<string>;
  readonly questionSubText: Signal<string>;
  readonly layout: Signal<QuizQuestionLayout | null>;
  readonly imageOptions: Signal<QuizOption[]>;
  readonly buttonOptions: Signal<QuizOption[]>;
  readonly textPlaceholder: Signal<string>;
  readonly textCtaOption: Signal<QuizOption | null>;
  readonly photoCtaOption: Signal<QuizOption | null>;
  readonly questionNumber: Signal<number>;
  readonly isThinking: Signal<boolean>;
  readonly isCompleted: Signal<boolean>;
  readonly errorMessage: Signal<string>;
  readonly requiresFallback: Signal<boolean>;
  readonly hasActiveQuestion: Signal<boolean>;
  /** Legacy engine only — the frontend-driven engine has no chat rows. */
  readonly messages?: Signal<unknown[]>;
  readonly questionOrder?: Signal<string[]>;
  /** Photos for a photo-upload question, resolved before the question is published. */
  readonly photoPair?: Signal<QuizPhotoPair | null>;
  readonly canGoBack?: Signal<boolean>;
  readonly currentAnswer?: Signal<string | string[] | null>;
  currentQuestionId(): string | undefined;
  /** False only when the user stepped back — the legacy engine cannot, so it is always true there. */
  isAtFurthestQuestion(): boolean;
  submitAnswer(message: string): Promise<QuizAnswerOutcome>;
  goBack?(): Promise<void>;
}
